import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ListBulletIcon } from "@heroicons/react/24/outline";
import {
  buildOfferLinesDisplayRows,
  DEFAULT_OFFER_LINES_VIEW_MODE,
} from "./utils/offer-lines-view";
import { applyPresentationRules } from "./utils/apply-presentation-rules";
import {
  chapterLabel,
  customerLineSum,
  formatMoney,
  formatQty,
  isGroupRow,
  isTextRow,
  isVisibleToCustomer,
  unitLabel,
} from "./offer-customer-lines";
import { safeHttpUrl } from "../../utils/sanitize-rich-html";
import { OfferViewportSummary } from "./offer-viewport-summary";
import { PromangRichTextContent } from "../../utils/PromangRichTextContent";
import "../../utils/PromangRichTextEditor.css";

/**
 * Filter to customer-visible lines for print/PDF preview.
 * Applies ADR-005 presentation_rules when provided, then viewMode grouping.
 */
export function filterItemsForPrintPreview(
  items = [],
  {
    groups = [],
    viewMode = DEFAULT_OFFER_LINES_VIEW_MODE,
    presentationRules = null,
    rootCalculationIds = [],
  } = {}
) {
  const mode = viewMode || DEFAULT_OFFER_LINES_VIEW_MODE;
  const presented =
    presentationRules != null
      ? applyPresentationRules(items, presentationRules, { rootCalculationIds })
      : items || [];
  const activeItems = (presented || []).filter((item) => !item?.delete);

  const rows = buildOfferLinesDisplayRows({
    items: activeItems,
    groups,
    viewMode: mode,
  });
  const filtered = [];
  rows.forEach((row) => {
    if (row.isChapterHeader) {
      filtered.push(row);
      return;
    }
    if (isVisibleToCustomer(row.item)) filtered.push(row);
  });

  // Drop chapter headers with no following visible product before next header
  const withContent = filtered.filter((row, index) => {
    if (!row.isChapterHeader) return true;
    for (let j = index + 1; j < filtered.length; j += 1) {
      if (filtered[j].isChapterHeader) return false;
      return true;
    }
    return false;
  });

  let end = withContent.length;
  while (end > 0 && withContent[end - 1].isChapterHeader) {
    end -= 1;
  }
  return withContent.slice(0, end);
}

export function sumVisibleCustomerLines(
  items = [],
  options = {}
) {
  return filterItemsForPrintPreview(items, options).reduce((acc, row) => {
    const item = row.item ?? row;
    if (
      row.isChapterHeader ||
      isGroupRow(item) ||
      isTextRow(item) ||
      ["optional", "excluded"].includes(item.pricing_role)
    ) {
      return acc;
    }
    const sum = customerLineSum(item);
    if (sum === null || !Number.isFinite(sum)) return acc;
    return acc + sum;
  }, 0);
}

/**
 * Read-only print-style customer lines table (no Synlig, no kost/DG).
 * Compact typography for A4 document preview and full-width on lines tab.
 * Default viewMode uses Offer::Group structure (calc headers as fallback).
 * Row kinds mirror PDF: chapter, note/text, product (+ info/image/URL).
 */
export default function OfferPrintLinesPreview({
  items = [],
  groups = [],
  viewMode = DEFAULT_OFFER_LINES_VIEW_MODE,
  presentationRules = null,
  rootCalculationIds = [],
  currency = "NOK",
  compact = false,
  showFooterTotal = true,
  className = "",
}) {
  const { t } = useTranslation();
  const previewOptions = useMemo(
    () => ({
      groups,
      viewMode,
      presentationRules,
      rootCalculationIds,
    }),
    [groups, viewMode, presentationRules, rootCalculationIds]
  );
  const displayRows = useMemo(
    () => filterItemsForPrintPreview(items, previewOptions),
    [items, previewOptions]
  );
  const footerTotal = useMemo(
    () => sumVisibleCustomerLines(items, previewOptions),
    [items, previewOptions]
  );

  const hasContent = displayRows.some(
    (row) => !row.isChapterHeader && (isTextRow(row.item) || !isGroupRow(row.item))
  );

  if (!hasContent) {
    const active = (items || []).filter((item) => !item?.delete);
    const hiddenCount = active.filter(
      (item) => !isGroupRow(item) && !isVisibleToCustomer(item)
    ).length;
    const emptyKey =
      active.length === 0
        ? "offers.printLinesPreview.empty_no_lines"
        : hiddenCount > 0
          ? "offers.printLinesPreview.empty_all_hidden"
          : "offers.printLinesPreview.empty";
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
        <ListBulletIcon className="h-7 w-7 text-slate-300" aria-hidden="true" />
        <p className="text-[12.5px] font-medium text-slate-600">
          {t(emptyKey)}
        </p>
        {hiddenCount > 0 ? (
          <p className="max-w-sm text-[12px] text-slate-500">
            {t("offers.printLinesPreview.empty_hidden_hint", { count: hiddenCount })}
          </p>
        ) : (
          <p className="max-w-sm text-[12px] text-slate-500">
            {t("offers.printLinesPreview.empty_hint")}
          </p>
        )}
      </div>
    );
  }

  const textSize = compact ? "text-[12px]" : "text-[12.5px]";

  return (
    <div
      className={
        className ||
        "offer-print-lines-preview flex h-full min-h-0 flex-col overflow-hidden bg-white"
      }
    >
      <div className="min-h-0 flex-1 overflow-auto">
        <table className={`min-w-full border-collapse ${textSize}`}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th
                scope="col"
                className="px-2.5 py-1.5 text-left text-[10px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.chapter")}
              </th>
              <th
                scope="col"
                className="px-2.5 py-1.5 text-right text-[10px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.qty")}
              </th>
              <th
                scope="col"
                className="px-2.5 py-1.5 text-left text-[10px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.unit")}
              </th>
              <th
                scope="col"
                className="px-2.5 py-1.5 text-right text-[10px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.unit_price")}
              </th>
              <th
                scope="col"
                className="px-2.5 py-1.5 text-right text-[10px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.line_sum")}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => {
              const item = row.item;
              const group = row.isChapterHeader;
              const note = !group && isTextRow(item);
              const { primary, secondary, ungrouped } = chapterLabel(
                item,
                group
              );
              const sum = group || note ? null : customerLineSum(item);
              const padLeft = 10 + (row.depth || 0) * 12;

              return (
                <tr
                  key={row.key}
                  className={
                    group
                      ? "border-b border-slate-100 bg-slate-50/70"
                      : note
                        ? "border-b border-slate-100 bg-slate-50/30"
                        : "border-b border-slate-100"
                  }
                >
                  <td
                    className="px-2.5 py-1.5"
                    style={{ paddingLeft: padLeft }}
                    colSpan={note ? 5 : undefined}
                  >
                    <div
                      className={
                        group
                          ? "font-semibold text-slate-900"
                          : note
                            ? "font-medium text-slate-700"
                            : "font-normal text-slate-800"
                      }
                    >
                      {ungrouped ? (
                        t("offers.tabs.lines_view.ungrouped")
                      ) : note ? (
                        <PromangRichTextContent
                          html={
                            item?.description ||
                            item?.title ||
                            primary ||
                            ""
                          }
                          className="text-sm text-slate-700"
                        />
                      ) : (
                        primary
                      )}
                    </div>
                    {!note && secondary ? (
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {secondary}
                      </div>
                    ) : null}
                    {!group && !note && safeHttpUrl(item?.product_image_url) ? (
                      <div className="mt-1.5">
                        <img
                          src={safeHttpUrl(item.product_image_url)}
                          alt=""
                          className="max-h-20 max-w-[100px] object-contain"
                        />
                      </div>
                    ) : null}
                    {!group && !note && item?.product_short_description ? (
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {item.product_short_description}
                      </div>
                    ) : null}
                    {!group && !note && safeHttpUrl(item?.product_url) ? (
                      <div className="mt-0.5 text-[11px]">
                        <a
                          href={safeHttpUrl(item.product_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1463C7] underline-offset-2 hover:underline"
                        >
                          {item.product_url}
                        </a>
                      </div>
                    ) : null}
                  </td>
                  {note ? null : (
                    <>
                      <td className="px-2.5 py-1.5 text-right tabular-nums text-slate-700">
                        {group || item?._hide_quantity ? "" : formatQty(item?.quantity)}
                      </td>
                      <td className="px-2.5 py-1.5 text-slate-500">
                        {group || item?._hide_unit ? "" : unitLabel(item)}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums text-slate-700">
                        {group || item?._hide_unit_price
                          ? ""
                          : formatMoney(item?.sales_price, currency)}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums font-semibold text-slate-900">
                        {sum === null ? "" : formatMoney(sum, currency)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showFooterTotal ? (
        <OfferViewportSummary>
        <div className="offer-lines-summary flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-2.5 py-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
            {t("offers.printLinesPreview.total")}
          </span>
          <span className="text-[12.5px] font-semibold tabular-nums text-slate-900">
            {formatMoney(footerTotal, currency)}
          </span>
        </div>
        </OfferViewportSummary>
      ) : null}
    </div>
  );
}
